import React, { FormEvent, useEffect, useId, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  ChevronDown,
  Crown,
  LogOut,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Search,
  X,
} from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { RECENT_ITEMS } from '../../data/dashboard-mock';
import { SAMPLE_INBOX, unreadCount } from '../../data/notifications-mock';
import { useLanguage } from '../../i18n/LanguageContext';
import { settingsShellCopy } from '../../pages/settings/settings-paths';
import ThemeToggle from '../../theme/ThemeToggle';
import { APP_NAV } from './app-nav';
import './app-shell.css';

const DESKTOP_QUERY = '(min-width: 960px)';
const SIDEBAR_KEY = 'mockloc-sidebar-collapsed';
const NOTE_PREVIEW = SAMPLE_INBOX.slice(0, 3);
const UNREAD_NOTIFICATIONS = unreadCount(SAMPLE_INBOX);

export interface ShellOutletContext {
  searchQuery: string;
}

function useDesktopLayout(): boolean {
  const [desktop, setDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(DESKTOP_QUERY).matches : true
  );

  useEffect(() => {
    const media = window.matchMedia(DESKTOP_QUERY);
    const onChange = () => setDesktop(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return desktop;
}

function firstName(fullName: string): string {
  const trimmed = fullName.trim();
  if (!trimmed) return '';
  return trimmed.split(/\s+/)[0];
}

function initial(fullName: string): string {
  const trimmed = fullName.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : 'M';
}

const AppShell: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { user, logout } = useAuth();
  const desktop = useDesktopLayout();
  const menuId = useId();
  const searchId = useId();
  const searchRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const notesRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(SIDEBAR_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [openMenu, setOpenMenu] = useState<'profile' | 'notes' | null>(null);

  const current =
    APP_NAV.find((item) =>
      item.end ? location.pathname === item.to : location.pathname.startsWith(item.to)
    ) ?? APP_NAV[0];

  const fullName = user?.userName?.trim() || '';
  const shortName = fullName ? firstName(fullName) : '';
  const isDashboard = location.pathname === '/app';
  const settingsCopy = settingsShellCopy(location.pathname);
  const heading = isDashboard
    ? t('shell.welcome', { name: shortName || t('shell.there') })
    : t(settingsCopy?.title ?? current.labelKey);
  const subtitle = isDashboard
    ? t('shell.dashboardSubtitle')
    : t(settingsCopy?.description ?? current.descriptionKey);

  const query = searchQuery.trim().toLowerCase();
  const pageHits = query
    ? APP_NAV.filter((item) => t(item.labelKey).toLowerCase().includes(query)).slice(0, 5)
    : [];
  const recentHits = query
    ? RECENT_ITEMS.filter((item) =>
        `${item.name} ${item.kindLabel}`.toLowerCase().includes(query)
      ).slice(0, 5)
    : [];
  const searchOpen = searchFocused && query.length > 0;

  useEffect(() => {
    setMenuOpen(false);
    setOpenMenu(null);
    setSearchFocused(false);
    setSearchQuery('');
  }, [location.pathname]);

  useEffect(() => {
    if (desktop) setMenuOpen(false);
  }, [desktop]);

  useEffect(() => {
    if (!menuOpen || desktop) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen, desktop]);

  useEffect(() => {
    if (!searchOpen && openMenu == null) return undefined;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (searchRef.current && !searchRef.current.contains(target)) {
        setSearchFocused(false);
      }
      if (profileRef.current && !profileRef.current.contains(target) && openMenu === 'profile') {
        setOpenMenu(null);
      }
      if (notesRef.current && !notesRef.current.contains(target) && openMenu === 'notes') {
        setOpenMenu(null);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSearchFocused(false);
        setOpenMenu(null);
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [searchOpen, openMenu]);

  const toggleCollapsed = () => {
    setCollapsed((currentState) => {
      const next = !currentState;
      try {
        window.localStorage.setItem(SIDEBAR_KEY, next ? '1' : '0');
      } catch {
        /* Ignore storage failures. The in-memory choice still applies. */
      }
      return next;
    });
  };

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  const goTo = (to: string) => {
    setSearchQuery('');
    setSearchFocused(false);
    setOpenMenu(null);
    navigate(to);
  };

  const onSearch = (event: FormEvent) => {
    event.preventDefault();
    const hits = [...pageHits, ...recentHits];
    if (hits.length > 0) goTo(hits[0].to);
  };

  const sidebarOpen = desktop || menuOpen;

  return (
    <div className={`shell${collapsed ? ' is-collapsed' : ''}`}>
      {menuOpen && !desktop && (
        <button
          type="button"
          className="shell-backdrop"
          aria-label={t('shell.closeMenu')}
          onClick={() => setMenuOpen(false)}
        />
      )}

      <aside
        id={menuId}
        className={`shell-sidebar${sidebarOpen ? ' is-open' : ''}`}
        aria-label={t('shell.accountMenu')}
      >
        <div className="shell-sidebar__brand">
          <NavLink to="/app" className="shell-brand" end>
            <img src="/mockloc-icon.png" alt="" className="shell-brand__logo" />
            <span className="shell-brand__copy">
              <span className="shell-brand__name">MockLoc</span>
              <span className="shell-brand__tag">{t('shell.tagline')}</span>
            </span>
          </NavLink>
          <button
            type="button"
            className="shell-collapse"
            aria-expanded={!collapsed}
            aria-label={collapsed ? t('shell.expandSidebar') : t('shell.collapseSidebar')}
            onClick={toggleCollapsed}
          >
            {collapsed ? (
              <PanelLeftOpen size={16} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={16} aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            className="shell-sidebar__close"
            aria-label={t('shell.closeMenu')}
            onClick={() => setMenuOpen(false)}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <nav className="shell-nav" aria-label={t('shell.mainNav')}>
          {APP_NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                title={t(item.labelKey)}
                className={({ isActive }) => `shell-link${isActive ? ' is-active' : ''}`}
              >
                <Icon size={18} strokeWidth={2} aria-hidden="true" />
                <span className="shell-link__label">{t(item.labelKey)}</span>
                {item.dot ? <span className="shell-link__dot" aria-hidden="true" /> : null}
              </NavLink>
            );
          })}
        </nav>

        <div className="shell-premium">
          <div className="shell-premium__copy">
            <span className="shell-premium__crown" aria-hidden="true">
              <Crown size={16} />
            </span>
            <p className="shell-premium__title">{t('shell.goPremium')}</p>
            <ul className="shell-premium__list">
              <li>
                <Check size={14} aria-hidden="true" />
                {t('shell.unlimitedCoins')}
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                {t('shell.unlimitedEverything')}
              </li>
              <li>
                <Check size={14} aria-hidden="true" />
                {t('shell.devicesLimit')}
              </li>
            </ul>
          </div>
          <NavLink to="/app/account" className="shell-premium__cta">
            <Crown size={16} className="shell-premium__cta-icon" aria-hidden="true" />
            <span>{t('shell.upgradeNow')}</span>
          </NavLink>
        </div>
      </aside>

      <div className="shell-body">
        <header className="shell-topbar">
          <div className="shell-topbar__lead">
            <button
              type="button"
              className="shell-menu-btn"
              aria-label={t('shell.openMenu')}
              aria-expanded={menuOpen}
              aria-controls={menuId}
              onClick={() => setMenuOpen(true)}
            >
              <Menu size={20} aria-hidden="true" />
            </button>
            <div className="shell-topbar__title">
              {isDashboard ? (
                <h1 className="shell-topbar__heading">
                  {heading}
                  <span className="shell-wave" aria-hidden="true">
                    👋
                  </span>
                </h1>
              ) : (
                <p className="shell-topbar__heading">{heading}</p>
              )}
              <p className="shell-topbar__sub">{subtitle}</p>
            </div>
          </div>

          <div className="shell-search" ref={searchRef}>
            <form role="search" onSubmit={onSearch}>
              <label className="visually-hidden" htmlFor={searchId}>
                {t('shell.searchLabel')}
              </label>
              <Search size={16} aria-hidden="true" />
              <input
                id={searchId}
                type="search"
                value={searchQuery}
                placeholder={t('shell.searchPlaceholder')}
                autoComplete="off"
                onChange={(event) => {
                  setSearchQuery(event.target.value);
                  setOpenMenu(null);
                }}
                onFocus={() => setSearchFocused(true)}
              />
            </form>
            {searchOpen && (
              <div className="shell-pop shell-search__panel" role="region" aria-label={t('shell.searchResults')}>
                {pageHits.length === 0 && recentHits.length === 0 && (
                  <p className="shell-pop__empty">{t('shell.noMatches')}</p>
                )}
                {pageHits.length > 0 && (
                  <>
                    <p className="shell-pop__label">{t('shell.pages')}</p>
                    {pageHits.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button key={item.to} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => goTo(item.to)}>
                          <Icon size={16} aria-hidden="true" />
                          {t(item.labelKey)}
                        </button>
                      );
                    })}
                  </>
                )}
                {recentHits.length > 0 && (
                  <>
                    <p className="shell-pop__label">{t('shell.recent')}</p>
                    {recentHits.map((item) => (
                      <button key={item.id} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => goTo(item.to)}>
                        {item.name}
                        <small>{item.kindLabel}</small>
                      </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          <div className="shell-tools">
            <ThemeToggle />
            <div className="shell-menu-anchor" ref={notesRef}>
              <button
                type="button"
                className="shell-icon-btn"
                aria-label={t('shell.notificationsUnread', { count: UNREAD_NOTIFICATIONS })}
                aria-expanded={openMenu === 'notes'}
                onClick={() => {
                  setSearchFocused(false);
                  setOpenMenu((currentMenu) => (currentMenu === 'notes' ? null : 'notes'));
                }}
              >
                <Bell size={18} aria-hidden="true" />
                {UNREAD_NOTIFICATIONS > 0 && (
                  <span className="shell-badge">{UNREAD_NOTIFICATIONS}</span>
                )}
              </button>
              {openMenu === 'notes' && (
                <div className="shell-pop shell-pop--notes" role="region" aria-label={t('shell.notifications')}>
                  {NOTE_PREVIEW.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      className={item.unread ? 'is-unread' : ''}
                      onClick={() => goTo('/app/notifications')}
                    >
                      <strong>{item.title}</strong>
                      <span>{item.body}</span>
                      <small>{item.time}</small>
                    </button>
                  ))}
                  <button type="button" className="shell-pop__all" onClick={() => goTo('/app/notifications')}>
                    {t('shell.viewAllNotifications')}
                  </button>
                </div>
              )}
            </div>

            <div className="shell-menu-anchor" ref={profileRef}>
              <button
                type="button"
                className="shell-profile"
                aria-label={t('shell.accountMenuNamed', { name: shortName || t('shell.account') })}
                aria-expanded={openMenu === 'profile'}
                onClick={() => {
                  setSearchFocused(false);
                  setOpenMenu((currentMenu) => (currentMenu === 'profile' ? null : 'profile'));
                }}
              >
                <span className="shell-profile__avatar" aria-hidden="true">
                  {initial(fullName)}
                </span>
                <span className="shell-profile__name">{shortName || t('shell.account')}</span>
                <ChevronDown size={16} className="shell-profile__chevron" aria-hidden="true" />
              </button>
              {openMenu === 'profile' && (
                <div className="shell-pop shell-pop--profile" role="region" aria-label={t('shell.account')}>
                  <div className="shell-pop__identity">
                    <strong>{fullName || t('shell.account')}</strong>
                    {user?.userEmail && <span>{user.userEmail}</span>}
                  </div>
                  <button type="button" onClick={() => goTo('/app/account')}>
                    {t('shell.account')}
                  </button>
                  <button type="button" className="shell-pop__logout" onClick={handleLogout}>
                    <LogOut size={16} aria-hidden="true" />
                    {t('shell.logOut')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="shell-content">
          <Outlet context={{ searchQuery } satisfies ShellOutletContext} />
        </main>
      </div>
    </div>
  );
};

export default AppShell;
